import { store } from '../../../../app/store';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import {
  employeeReviewApi,
  type SubmitParticipantFeedbackRequest,
} from '../../../employee-review/api/employeeReviewApi';
import type { CommandHandler } from '../types';

const getPersistedReviewId = (context: Parameters<CommandHandler>[1]) => {
  const reviewId = Number(context.entityId ?? context.parentId);
  return Number.isInteger(reviewId) && reviewId > 0 ? reviewId : null;
};

const ensureSaved = (context: Parameters<CommandHandler>[1], message: string) => {
  if (!getPersistedReviewId(context)) return message;
  if (context.hasUnsavedChanges || context.hasUnsavedChildChanges) {
    return 'Save all changes before continuing.';
  }
  return null;
};

const navigateToSourcePage = (context: Parameters<CommandHandler>[1]) => {
  window.setTimeout(() => {
    const currentPath = context.currentPath.replace(/\/+$/, '');
    const lastSlashIndex = currentPath.lastIndexOf('/');
    context.navigate(lastSlashIndex > 0 ? currentPath.slice(0, lastSlashIndex) : '/');
  }, 600);
};

const submitReview =
  (endpoint: 'submitEmployeeReview' | 'approveEmployeeReview', fallback: string): CommandHandler =>
  async (_command, context) => {
    const error = ensureSaved(context, 'Open a saved review first.');
    if (error) return { success: false, error };

    try {
      const reviewId = getPersistedReviewId(context)!;
      const message = await store
        .dispatch(
          employeeReviewApi.endpoints[endpoint].initiate({ reviewId, preferSpaLinks: true })
        )
        .unwrap();
      navigateToSourcePage(context);
      return { success: true, message };
    } catch (requestError) {
      return { success: false, error: extractErrorMessage(requestError, fallback) };
    }
  };

const getNumber = (values: Record<string, unknown>, field: string) => Number(values[field]);

export const buildParticipantFeedbackRequest = (
  values: Record<string, unknown>,
  reviewId: number
): SubmitParticipantFeedbackRequest => {
  const ratings = {
    teamwork: getNumber(values, 'Teamwork'),
    communication: getNumber(values, 'Communication'),
    helpfulness: getNumber(values, 'Helpfulness'),
    reliability: getNumber(values, 'Reliability'),
  };
  const professionalLevel = getNumber(values, 'ProfessionalLevel');
  if (
    [...Object.values(ratings), professionalLevel].some(
      (rating) => !Number.isInteger(rating) || rating < 1 || rating > 5
    )
  ) {
    throw new Error('All ratings must be whole numbers from 1 to 5.');
  }

  const targetId = String(values.TargetCrmRelationId ?? '').trim();

  return {
    ...(targetId ? { targetId } : {}),
    hrmEmployeeReviewId: reviewId,
    ratings,
    professionalLevel,
    feedback: String(values.Feedback ?? ''),
  };
};

export const employeeReviewSubmitCommandHandler = submitReview(
  'submitEmployeeReview',
  'Failed to submit review.'
);

export const employeeReviewApproveByManagerCommandHandler = submitReview(
  'approveEmployeeReview',
  'Failed to approve review.'
);

export const toggleGridVisibilityFilterCommandHandler: CommandHandler = (command, context) => {
  if (!context.toggleGridVisibilityFilter) {
    return { success: false, error: 'Visibility filtering is not available here.' };
  }

  context.toggleGridVisibilityFilter(command);
  return { success: true };
};

export const employeeReviewParticipantFeedbackSubmitCommandHandler: CommandHandler = async (
  _command,
  context
) => {
  const reviewId = getPersistedReviewId(context);
  if (!reviewId) return { success: false, error: 'Open a saved review first.' };

  let request: SubmitParticipantFeedbackRequest;
  try {
    request = buildParticipantFeedbackRequest(context.entityData ?? {}, reviewId);
  } catch (validationError) {
    return {
      success: false,
      error: extractErrorMessage(validationError, 'Failed to validate feedback.'),
    };
  }
  try {
    const message = await store
      .dispatch(employeeReviewApi.endpoints.submitParticipantFeedback.initiate(request))
      .unwrap();
    navigateToSourcePage(context);
    return { success: true, message };
  } catch (requestError) {
    return {
      success: false,
      error: extractErrorMessage(requestError, 'Failed to submit feedback.'),
    };
  }
};
